(() => {
  if (window.RNFRecentlyViewed) { window.RNFRecentlyViewed.mount(document); return; }
  const KEY = 'rnf:recently-viewed:v1';
  const TTL = 30 * 24 * 60 * 60 * 1000;
  const LIMIT = 12;
  const roots = new Map();
  const requests = [];
  let pending = 0;
  let fallbackFrame = 0;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,159}$/.test(value);

  function allowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return typeof privacy?.preferencesProcessingAllowed !== 'function' || privacy.preferencesProcessingAllowed();
    } catch { return false; }
  }

  function history() {
    if (!allowed()) return [];
    try {
      const parsed = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (parsed?.version !== 1 || !Array.isArray(parsed.items)) return [];
      const now = Date.now(), ids = new Set(), handles = new Set();
      const items = parsed.items.filter(item => validHandle(item?.handle) && /^\d+$/.test(String(item.id)) && Number.isFinite(item.seenAt) && item.seenAt > now - TTL && item.seenAt <= now + 60000)
        .sort((a, b) => b.seenAt - a.seenAt).filter(item => {
          const id = String(item.id);
          if (ids.has(id) || handles.has(item.handle)) return false;
          ids.add(id); handles.add(item.handle); return true;
        }).slice(0, LIMIT).map(item => ({ id: String(item.id), handle: item.handle, seenAt: item.seenAt }));
      if (JSON.stringify(items) !== JSON.stringify(parsed.items)) {
        try { localStorage.setItem(KEY, JSON.stringify({ version: 1, items })); } catch {}
      }
      return items;
    } catch { return []; }
  }

  function track() {
    if (!allowed()) return;
    const metadata = document.querySelector('[data-rnf-recent-visit]');
    if (!metadata) return;
    try {
      const item = JSON.parse(metadata.textContent);
      if (!validHandle(item.handle) || !/^\d+$/.test(String(item.id))) return;
      const visit = { id: String(item.id), handle: item.handle, seenAt: Date.now() };
      const items = [visit, ...history().filter(old => old.id !== visit.id && old.handle !== visit.handle)].slice(0, LIMIT);
      localStorage.setItem(KEY, JSON.stringify({ version: 1, items }));
    } catch {}
  }

  function productCard(html, entry) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const template = parsed.querySelector('template[data-rnf-recent-product]');
    if (!template || (template.dataset.productId && template.dataset.productId !== entry.id)) return null;
    if (!template.dataset.productId && template.dataset.productHandle !== entry.handle) return null;
    const source = template.content.firstElementChild;
    if (!source || (!source.matches('li.grid__item,.rnf-card-wrap,.rnf-nav-card') && !source.querySelector('.rnf-nav-card'))) return null;
    const node = document.importNode(source, true);
    node.querySelectorAll('script').forEach(script => script.remove());
    if (node.matches('li.grid__item')) return node;
    const li = document.createElement('li'); li.className = 'grid__item';
    if (node.matches('.rnf-card-wrap')) li.append(node);
    else { const wrap = document.createElement('div'); wrap.className = 'rnf-card-wrap'; wrap.append(node); li.append(wrap); }
    return li;
  }

  function pump() {
    while (pending < 2 && requests.length) {
      const task = requests.shift();
      if (task.signal.aborted) { task.resolve(null); continue; }
      pending++;
      const controller = new AbortController();
      const abort = () => controller.abort();
      task.signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 12000);
      fetch(task.url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then(response => response.ok ? response.text() : '')
        .then(html => task.resolve(controller.signal.aborted ? null : productCard(html, task.entry)))
        .catch(() => task.resolve(null))
        .finally(() => {
          clearTimeout(timeout); task.signal.removeEventListener('abort', abort); pending--; pump();
        });
    }
  }

  function requestCard(url, entry, signal) {
    return new Promise(resolve => { requests.push({ url, entry, signal, resolve }); pump(); });
  }

  const viewport = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const controller = roots.get(entry.target);
      if (!controller) return;
      controller.inView = entry.isIntersecting && entry.intersectionRatio > 0;
      if (controller.inView) controller.load();
    });
  }, { rootMargin: '0px', threshold: 0 }) : null;

  class Recent {
    constructor(root) {
      this.root = root; this.grid = root.querySelector('[data-rnf-recent-grid]');
      this.buttons = [...root.querySelectorAll('[data-rnf-recent-step]')];
      this.version = 0; this.frame = 0; this.max = 0; this.step = 0; this.inView = false;
      this.signature = null; this.entries = []; this.loading = false; this.loaded = false; this.dead = false;
      this.onScroll = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); }); };
      this.onClick = event => {
        const button = event.target.closest('[data-rnf-recent-step]');
        if (!button || !root.contains(button) || button.disabled) return;
        const sign = getComputedStyle(this.grid).direction === 'rtl' ? -1 : 1;
        const target = Math.max(0, Math.min(this.max, Math.abs(this.grid.scrollLeft) + Number(button.dataset.rnfRecentStep) * this.step));
        this.grid.scrollTo({ left: sign * target, behavior: reduced.matches ? 'instant' : 'smooth' });
      };
      this.onResize = () => this.measure();
      this.grid.addEventListener('scroll', this.onScroll, { passive: true });
      root.addEventListener('click', this.onClick);
      this.resize = typeof ResizeObserver === 'function' ? new ResizeObserver(this.onResize) : null;
      this.resize?.observe(this.grid);
      if (!this.resize) window.addEventListener('resize', this.onResize);
    }

    event(type) { this.grid.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { root: this.grid } })); }

    refresh() {
      const limit = Math.max(1, Math.min(LIMIT, Number(this.root.dataset.limit) || 8));
      const entries = history().slice(0, limit);
      const signature = entries.map(entry => `${entry.id}:${entry.handle}`).join('|');
      if (signature === this.signature) return;
      this.signature = signature; this.entries = entries; this.version++; this.abort?.abort();
      this.loading = false; this.loaded = false;
      if (this.grid.children.length) this.event('rnf:cards:unmount');
      this.grid.replaceChildren(); this.root.hidden = !entries.length;
      this.root.toggleAttribute('data-rnf-recent-pending', entries.length > 0);
      this.grid.setAttribute('aria-busy', String(entries.length > 0));
      this.measure();
      if (this.inView) this.load();
    }

    async load() {
      if (this.dead || this.loaded || this.loading || !this.entries.length || !this.inView || document.hidden || !allowed()) return;
      let rootURL;
      try {
        rootURL = new URL((this.root.dataset.rootUrl || '/').replace(/\/?$/, '/'), location.origin);
        if (rootURL.origin !== location.origin) throw new Error('Invalid product origin');
      } catch {
        this.root.hidden = true; this.loaded = true;
        this.root.removeAttribute('data-rnf-recent-pending'); this.grid.setAttribute('aria-busy', 'false');
        return;
      }
      this.loading = true;
      const version = this.version, abort = this.abort = new AbortController();
      const cards = await Promise.all(this.entries.map(entry => {
        const url = new URL(`products/${encodeURIComponent(entry.handle)}`, rootURL);
        url.searchParams.set('section_id', 'rnf-recently-viewed');
        return requestCard(url.href, entry, abort.signal);
      }));
      if (this.dead || abort.signal.aborted || version !== this.version || !this.root.isConnected) return;
      const valid = cards.filter(Boolean);
      this.grid.replaceChildren(...valid); this.loading = false; this.loaded = true;
      this.root.removeAttribute('data-rnf-recent-pending'); this.grid.setAttribute('aria-busy', 'false');
      this.root.hidden = valid.length === 0;
      if (valid.length) this.event('rnf:cards:mount');
      this.measure();
    }

    measure() { this.max = Math.max(0, this.grid.scrollWidth - this.grid.clientWidth); this.step = this.grid.clientWidth; this.update(); }
    update() {
      const offset = Math.abs(this.grid.scrollLeft);
      this.buttons.forEach(button => {
        const visible = !this.root.hidden && this.max > 2 && (Number(button.dataset.rnfRecentStep) < 0 ? offset > 2 : offset < this.max - 2);
        if (button.dataset.visible === String(visible)) return;
        button.dataset.visible = String(visible); button.disabled = !visible; button.setAttribute('aria-hidden', String(!visible));
      });
    }
    destroy() {
      this.dead = true; this.version++; this.abort?.abort(); cancelAnimationFrame(this.frame); this.resize?.disconnect();
      viewport?.unobserve(this.root); this.grid.removeEventListener('scroll', this.onScroll); this.root.removeEventListener('click', this.onClick);
      window.removeEventListener('resize', this.onResize);
      if (this.grid.children.length) this.event('rnf:cards:unmount');
      roots.delete(this.root);
    }
  }

  function mount(scope) {
    const elements = [...(scope.matches?.('[data-rnf-recently-viewed]') ? [scope] : []), ...scope.querySelectorAll('[data-rnf-recently-viewed]')];
    elements.forEach(root => {
      if (roots.has(root) || !root.querySelector('[data-rnf-recent-grid]')) return;
      const controller = new Recent(root); roots.set(root, controller); controller.refresh(); viewport?.observe(root);
    });
    if (!viewport) fallback();
  }
  function fallback() {
    if (fallbackFrame) return;
    fallbackFrame = requestAnimationFrame(() => {
      fallbackFrame = 0;
      roots.forEach(controller => {
        const box = controller.root.getBoundingClientRect();
        controller.inView = !controller.root.hidden && box.bottom > 0 && box.top < innerHeight;
        if (controller.inView) controller.load();
      });
    });
  }
  function refresh() { roots.forEach(controller => controller.refresh()); if (!viewport) fallback(); }
  window.RNFRecentlyViewed = { mount };
  track(); mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => [...roots.values()].forEach(controller => { if (event.target.contains(controller.root)) controller.destroy(); }));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) roots.forEach(controller => { if (controller.inView) controller.load(); }); });
  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) { try { localStorage.removeItem(KEY); } catch {} }
    else track();
    refresh();
  });
  window.addEventListener('pageshow', event => { if (event.persisted) { track(); refresh(); } });
  window.addEventListener('storage', event => { if (event.key === KEY) refresh(); });
  if (!viewport) { window.addEventListener('scroll', fallback, { passive: true }); window.addEventListener('resize', fallback, { passive: true }); }
})();
